/* eslint-disable no-continue */
import { DEFAULT_IMAGE_FORMAT, IMAGE_FIT_SCALE_CROP, IMAGE_QUALITY, RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import { imageService, LAYOUT_ENTITIES } from '@bedrock/fido-sdk';
import { format } from 'date-fns';
import { featureFlipping } from '@bedrock-rdk/config';
import { t } from '../../../../i18n';
import { getScreenSize } from '../../../utils/sizes';
import { getNestedOriginalTarget } from '../../../../../logic/lock/lock.utils';
import { sizes, templates } from '../../../square/square.constants';
import {
  ABBREVIATION_I18N_KEY,
  COMPLETE_I18N_KEY,
  DAYS_OF_THE_WEEK_I18N,
  MONTHS_I18N_KEY,
  START_HOUR,
} from './epg.constants';

export const getChannelContent = (base, size) => {
  if (!base) {
    return {};
  }

  const image = base.image || {};
  const sizeFallback = size || sizes[templates.Square][RENDERING_CONTEXT.GENERAL][getScreenSize()].w;

  return {
    ...base,
    image: {
      ...image,
      src: imageService.getUrl(base.image.id, sizeFallback, sizeFallback, {
        fit: IMAGE_FIT_SCALE_CROP,
        quality: IMAGE_QUALITY,
        format: DEFAULT_IMAGE_FORMAT,
      }),
    },
  };
};

export const makePlaceholder = (start, end) => ({
  start: {
    date: start,
    title: format(start, 'HH:mm'),
  },
  end: {
    date: end,
    title: format(end, 'HH:mm'),
  },
  title: t('epgGrid.noProgram'),
});

export const trimEpg = (row, min, max) => {
  const programs = row.map(({ start, end, ...rest }) => {
    const startDate = new Date(start.date);
    const endDate = new Date(end.date);

    return {
      start: {
        date: startDate,
        title: format(startDate, 'HH:mm'),
      },
      end: {
        date: endDate,
        title: format(endDate, 'HH:mm'),
      },
      ...rest,
    };
  });

  const result = [];
  const lastIndex = programs.length - 1;
  let tail = min;

  for (let i = 0; i < programs.length; i += 1) {
    const program = { ...programs[i] };
    const end = program.end.date;
    if (end <= min) {
      continue;
    }

    const start = program.start.date;
    if (start >= max) {
      if (tail < max) {
        result.push(makePlaceholder(tail, max));
      }

      break;
    }

    if (start < min) {
      program.start.date = min;
    }

    if (end > max) {
      program.end.date = max;
    }

    if (start > tail) {
      result.push(makePlaceholder(tail, start));
    }

    result.push(program);

    tail = end;

    if (i === lastIndex) {
      if (end < max) {
        result.push(makePlaceholder(end, max));
      }
    }
  }

  return result;
};

export const offsetFromStart = (start, hour) => start + (hour - START_HOUR) * 60 * 60 * 1000;

export const formatDate = (date, abbreviated = false) => {
  const day = date.getDay();
  const month = date.getMonth();
  const tail = abbreviated ? ABBREVIATION_I18N_KEY : COMPLETE_I18N_KEY;
  const dayKey = `${DAYS_OF_THE_WEEK_I18N[day]}.${tail}`;
  const monthKey = `${MONTHS_I18N_KEY[month]}.${tail}`;

  return `${t(dayKey)}, ${date.getDate()}. ${t(monthKey)}`;
};

export const isActionAllowed = action => {
  if (!featureFlipping.liveSuppressionOnEPGFeature || !action) {
    return true;
  }

  const target = getNestedOriginalTarget(action.target);

  // Could be modal action, just allowing the app to handle it
  if (!target) {
    return true;
  }

  return !target.startsWith(LAYOUT_ENTITIES.live);
};
